import React from 'react';
import { Link } from 'react-router-dom';
import { FileSignature, FileText, CalendarCheck2, ArrowUpRight } from 'lucide-react';

const buildGuestPrefillQuery = (lead) => {
  const params = new URLSearchParams();
  const name = lead?.guest_name || lead?.client_display_name || lead?.client_name || '';
  const email = lead?.guest_email || lead?.client_display_email || lead?.client_email || '';
  const phone = lead?.guest_phone || lead?.client_display_phone || lead?.client_phone || '';
  const eventDate = lead?.event_date || lead?.scheduled_date || '';
  const eventType = lead?.event_type || '';
  if (name) params.set('guest_name', String(name));
  if (email) params.set('guest_email', String(email));
  if (phone) params.set('guest_phone', String(phone));
  if (eventDate) params.set('event_date', String(eventDate).slice(0, 10));
  if (eventType) params.set('event_type', String(eventType));
  if (lead?.id) params.set('lead_id', String(lead.id));
  const qs = params.toString();
  return qs ? `&${qs}` : '';
};

/**
 * Interconnect panel: Lead ↔ Booking ↔ Invoice ↔ Contract
 */
const LeadWorkspaceLinks = ({ lead, channel = 'external' }) => {
  const links = lead?.links || {};
  const bookingId = links.booking_id || lead?.converted_booking_id || lead?.booking_id;
  const guestQs = buildGuestPrefillQuery(lead);
  const invoiceListBase =
    channel === 'internal' ? '/vendor/internal-invoices' : '/vendor/invoices';

  const invoiceCreateFallback = `/vendor/invoices/create?channel=${channel}${
    bookingId ? `&booking_id=${bookingId}` : ''
  }${guestQs}`;
  const contractCreateFallback = `/vendor/contracts/create${
    bookingId ? `?booking_id=${bookingId}` : ''
  }${guestQs ? (bookingId ? guestQs : `?${guestQs.slice(1)}`) : ''}`;

  const invoiceHref =
    links.invoice_href ||
    (lead?.primary_invoice_id
      ? `${invoiceListBase}/${lead.primary_invoice_id}`
      : invoiceCreateFallback);
  const contractHref =
    links.contract_href ||
    (lead?.primary_contract_id
      ? `/vendor/contracts/${lead.primary_contract_id}`
      : contractCreateFallback);
  const bookingHref =
    links.booking_href ||
    (bookingId ? `/vendor/bookings/${bookingId}` : null);

  const items = [
    {
      key: 'booking',
      label: bookingId ? 'Open booking' : 'No booking yet',
      hint: bookingId ? 'Linked private / marketplace booking' : 'Convert or mark booked to connect',
      href: bookingHref,
      Icon: CalendarCheck2,
      disabled: !bookingHref,
    },
    {
      key: 'invoice',
      label: lead?.primary_invoice_id ? 'Open invoice' : 'Create invoice',
      hint: bookingId
        ? 'Tied to this lead’s booking'
        : 'Guest details copy over to the invoice',
      href: invoiceHref,
      Icon: FileText,
      disabled: false,
    },
    {
      key: 'contract',
      label: lead?.primary_contract_id ? 'Open contract' : 'Create contract',
      hint: bookingId
        ? 'Prefilled from booking when supported'
        : 'Guest details copy over to the contract',
      href: contractHref,
      Icon: FileSignature,
      disabled: false,
    },
  ];

  return (
    <div className="space-y-2">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-ink-tertiary">
        Connected work
      </p>
      <div className="grid gap-2 sm:grid-cols-3">
        {items.map(({ key, label, hint, href, Icon, disabled }) => (
          <Link
            key={key}
            to={disabled ? '#' : href}
            onClick={(e) => disabled && e.preventDefault()}
            className={`rounded-lg border border-border bg-surface p-3 transition ${
              disabled ? 'pointer-events-none opacity-50' : 'hover:border-ink/30 hover:bg-surface-muted'
            }`}
          >
            <div className="mb-2 flex items-center justify-between gap-2">
              <Icon className="h-4 w-4 text-ink-tertiary" />
              {!disabled ? <ArrowUpRight className="h-3.5 w-3.5 text-ink-tertiary" /> : null}
            </div>
            <p className="text-xs font-semibold text-ink">{label}</p>
            <p className="mt-0.5 text-[11px] text-ink-tertiary">{hint}</p>
          </Link>
        ))}
      </div>
    </div>
  );
};

export default LeadWorkspaceLinks;
